import { useLogout, usePermissions } from '@boothiva/auth';
import { useQuery } from '@tanstack/react-query';
import { AppShell, Breadcrumbs, Sidebar, ThemeToggle, Topbar } from '@boothiva/ui';
import { Building2, CreditCard, Download, Flag, Gauge, LogOut, Megaphone, Moon, PieChart, Settings, ShieldCheck, SlidersHorizontal } from 'lucide-react';
import { Link, Outlet, useLocation, useMatches } from 'react-router';
import { getWorkerSleep } from '../../features/settings/api';
import lockup from '../../assets/boothiva-lockup.png';
import lockupReverse from '../../assets/boothiva-lockup-reverse.webp';
type RouteHandle = { crumb?: string };

/* Both lockups ship; CSS shows the one that suits the theme. "Control" stays as text beside the mark so
   staff can tell at a glance which portal they are in. */
const brand = <span className="bv-brand-lockup">
  <img className="bv-logo bv-logo--light" src={lockup} alt="Boothiva" width={280} height={86} />
  <img className="bv-logo bv-logo--reverse" src={lockupReverse} alt="" aria-hidden="true" width={280} height={86} />
  <span className="bv-brand-lockup__suffix">Control</span>
</span>;
/**
 * Says so, everywhere, while background processing is switched off. Asleep, nothing sends a password
 * reset, expires a gallery or reconciles Stripe, and all of that fails silently -- there is no bounce and
 * no error to notice. A banner on every page is the cost of having a button that can cause it.
 *
 * <p>Polled rarely, and only while this tab has focus. The read itself is a file on the server, but every
 * authenticated request validates a session against the database -- so a frequent poll from an admin tab
 * left open is itself enough to keep awake the database this feature exists to let sleep. Five minutes is
 * fast enough for a banner that only has to catch up with someone waking it elsewhere; pressing the button
 * here updates it immediately either way.</p>
 */
function BackgroundSleepBanner() {
  const query = useQuery({
    queryKey: ['platform', 'worker-sleep'],
    queryFn: getWorkerSleep,
    refetchInterval: 300_000,
    refetchIntervalInBackground: false,
  });
  if (!query.data?.sleeping) return null;
  return <div className="bv-sleep-banner" role="status">
    <Moon aria-hidden="true" size={16} />
    <span><strong>Background processing is asleep.</strong> No emails, gallery expiry, retention sweeps or Stripe reconciliation are running.</span>
    <Link to="/settings">Wake it</Link>
  </div>;
}

export function AppLayout() {
  const location = useLocation(); const matches = useMatches();
  const logout = useLogout('/admin/login');
  const { has } = usePermissions();
  const crumbs = matches.map((match) => ({ handle: match.handle as RouteHandle, pathname: match.pathname })).filter((match) => match.handle.crumb).map((match) => ({ label: match.handle.crumb!, href: match.pathname }));
  const items = [
    { href: '/', label: 'Dashboard', icon: <Gauge aria-hidden="true" size={20} />, current: location.pathname === '/' },
    { href: '/customers', label: 'Customers', icon: <Building2 aria-hidden="true" size={20} />, current: location.pathname.startsWith('/customers') },
    { href: '/plans', label: 'Plans', icon: <CreditCard aria-hidden="true" size={20} />, current: location.pathname.startsWith('/plans') },
    { href: '/statistics/events', label: 'Statistics', icon: <PieChart aria-hidden="true" size={20} />, current: location.pathname.startsWith('/statistics') },
    { href: '/announcements', label: 'Announcements', icon: <Megaphone aria-hidden="true" size={20} />, current: location.pathname.startsWith('/announcements') },
    ...(has('platform.releases.manage') ? [{ href: '/kiosk-releases', label: 'Kiosk releases', icon: <Download aria-hidden="true" size={20} />, current: location.pathname.startsWith('/kiosk-releases') }] : []),
    {
      type: 'group' as const, label: 'Settings', icon: <Settings aria-hidden="true" size={20} />,
      children: [
        { href: '/settings', label: 'General', icon: <SlidersHorizontal aria-hidden="true" size={16} />, current: location.pathname === '/settings' },
        { href: '/settings/security', label: 'Security', icon: <ShieldCheck aria-hidden="true" size={16} />, current: location.pathname.startsWith('/settings/security') },
      ],
    },
    { href: '/feature-flags', label: 'Feature flags', icon: <Flag aria-hidden="true" size={20} />, current: location.pathname.startsWith('/feature-flags') },
  ];
  const appFooter = <>
    <span className="bv-app-footer__brand">{brand}</span>
    <span>© {new Date().getFullYear()} Boothiva. Platform administration.</span>
    <span>Developed by <a href="https://diyonix.com.au" target="_blank" rel="noreferrer">Diyonix</a></span>
    <nav aria-label="Reference">
      <a href="https://boothiva.com/legal/sub-processors">Sub-processors</a>
      <a href="https://boothiva.com/legal/privacy">Privacy</a>
    </nav>
  </>;
  return <div className="bv-admin-app"><BackgroundSleepBanner /><AppShell footer={appFooter} sidebar={<Sidebar brand={brand} items={items} />} topbar={<Topbar admin breadcrumbs={<Breadcrumbs items={crumbs} />} actions={<><ThemeToggle /><button type="button" className="bv-button bv-button--quiet" onClick={() => void logout()}><LogOut aria-hidden="true" size={16} />Log out</button></>} />}><Outlet /></AppShell></div>;
}
